import { IMaintenance } from '@alberta/konexi-shared';
import { Inject, Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import hash from 'object-hash';
import { from } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { IShutdownBroker } from 'src/app/business/maintenance/contracts/shutdown-broker';
import { IShutdownInfo } from 'src/app/business/maintenance/contracts/shutdown-info';
import { SessionController } from 'src/app/auth/session/session-controller';
import { ShutdownBroker } from 'src/app/shared/services/maintenance/shutdown-broker.service';

import { SelectModalComponent } from '../../common/select-modal/select-modal.component';
import { MaintenanceInfoModalComponent } from './maintenance-info-modal';

/**
 * Shows the "Wartungshinweis" modal, once per announcement.
 *
 * The `ShutdownBroker` publishes a shutdown info whenever a maintenance window becomes relevant:
 * when it starts (also when the app starts inside a running window), and when it ends. The
 * `MaintenanceModeChecker` marks the infos a user should be told about with `showPopUp`; the
 * countdown snack bar covers the rest.
 *
 * Two things keep the modal from nagging:
 * - it is only armed once the user is in the app (`whenAuthenticated`) — a shutdown info can only
 *   arrive after that anyway, because the maintenance data is synced by the pipeline, but the
 *   modal must never appear over the login page;
 * - every info is shown once per process. Infos are compared by content (`object-hash`), so the
 *   same window re-published by a later sync is skipped, while a rescheduled window — different
 *   start or end — is announced again.
 *
 * A new modal replaces an open one, so a user who did not confirm the start sees the end.
 */
@Injectable({ providedIn: 'root' })
export class MaintenanceInfoService {
  private _maintenanceModal: HTMLIonModalElement;
  private readonly _shown = new Set<string>();
  /** Modals open one after the other, so a newer announcement always finds the older modal to replace. */
  private _opening: Promise<void> = Promise.resolve();

  constructor(
    private _modalController: ModalController,
    private _session: SessionController,
    @Inject(ShutdownBroker) private _shutdownBroker: IShutdownBroker
  ) {
    from(this._session.whenAuthenticated())
      .pipe(
        switchMap(() => this._shutdownBroker.shutdownWatch),
        filter(
          shutdownInfo => shutdownInfo?.showPopUp && !!shutdownInfo.maintenance && !shutdownInfo.maintenance.stopped
        ),
        filter(shutdownInfo => this.notShownYet(shutdownInfo))
      )
      .subscribe(shutdownInfo => {
        this._opening = this._opening.then(() => this.openInfoModal(shutdownInfo.maintenance)).catch(console.error);
      });
  }

  private notShownYet(shutdownInfo: IShutdownInfo): boolean {
    const key = hash(shutdownInfo);
    if (this._shown.has(key)) {
      return false;
    }
    this._shown.add(key);
    return true;
  }

  private async openInfoModal(maintenance: IMaintenance): Promise<void> {
    if (this._maintenanceModal) {
      this._maintenanceModal.dismiss().catch(console.error);
    }

    this._maintenanceModal = await this._modalController.create({
      backdropDismiss: false,
      component: SelectModalComponent,
      componentProps: {
        header: 'Wartungshinweis',
        buttonText: 'OK, verstanden',
        _content: MaintenanceInfoModalComponent,
        _dontCheckForChange: true,
        _componentProps: {
          startDate: maintenance.start,
        },
      },
      cssClass: 'selectModal maintenanceInfoModal',
    });
    await this._maintenanceModal.present();
  }
}
